import { Component, type ErrorInfo, type ReactNode } from "react";

import { SUPPORT_EMAIL } from "@/lib/support";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches any uncaught render error anywhere in the React tree below it
 * and shows a Spanish fallback screen with a "Reportar error" button that
 * opens the user's mail client with a pre-filled message to
 * {@link SUPPORT_EMAIL}.
 *
 * Without this boundary a single render throw would unmount the whole
 * tree and produce a blank white screen until the user refreshes.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Also surface the error in the browser console so devs tailing DevTools
    // still see the full stack. React swallows it after getDerivedStateFromError.
     
    console.error("[ErrorBoundary] Caught render error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private buildMailtoHref(): string {
    const { error, errorInfo } = this.state;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
    const timestamp = new Date().toISOString();
    const stack = error?.stack?.trim() ?? "(sin stack)";
    const componentStack = errorInfo?.componentStack?.trim() ?? "(sin component stack)";
    const message = error?.message ?? "(sin mensaje)";

    const subject = `[Gestionalo Portal] Error: ${message.slice(0, 80)}`;
    const body =
      `Hola equipo de soporte,\n\n` +
      `El portal mostró un error inesperado. Detalles técnicos abajo para ayudarlos a diagnosticarlo.\n\n` +
      `— Mensaje: ${message}\n` +
      `— URL: ${url}\n` +
      `— Fecha: ${timestamp}\n` +
      `— Navegador: ${userAgent}\n\n` +
      `Stack trace:\n${stack}\n\n` +
      `Component stack:\n${componentStack}\n`;

    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f9fafb",
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 32,
            boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
          }}
        >
          <h1
            style={{
              margin: "0 0 12px 0",
              fontSize: 22,
              lineHeight: 1.3,
              color: "#111827",
              fontWeight: 600,
            }}
          >
            Algo salió mal
          </h1>
          <p
            style={{
              margin: "0 0 20px 0",
              fontSize: 15,
              lineHeight: 1.5,
              color: "#4b5563",
            }}
          >
            Ocurrió un error inesperado y la página no pudo continuar. Podés
            recargar para volver a intentarlo. Si el problema persiste,
            reportalo a soporte con el botón de abajo — el mensaje incluye los
            datos técnicos que necesitamos para ayudarte.
          </p>

          {error.message ? (
            <pre
              style={{
                margin: "0 0 20px 0",
                padding: "12px 14px",
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.4,
                color: "#374151",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              }}
            >
              {error.message}
            </pre>
          ) : null}

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 500,
                color: "#ffffff",
                background: "#111827",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Recargar página
            </button>
            <a
              href={this.buildMailtoHref()}
              style={{
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 500,
                color: "#111827",
                background: "#ffffff",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Reportar error a soporte
            </a>
          </div>
        </div>
      </div>
    );
  }
}
